import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  ShieldAlertIcon,
  SearchIcon,
  FilterIcon,
  RefreshCwIcon,
  LogInIcon,
  LogOutIcon,
  UserIcon,
  BuildingIcon,
  CalendarIcon,
  GlobeIcon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImpersonationLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchAdmin, setSearchAdmin] = useState('');
  const [searchTarget, setSearchTarget] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [pagination, setPagination] = useState({ total: 0, skip: 0, limit: 100 });

  // Fetch logs
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ultra_admin_token');
      
      // Build query params
      const params = new URLSearchParams();
      params.append('limit', pagination.limit);
      params.append('skip', pagination.skip);
      if (actionFilter !== 'all') {
        params.append('action', actionFilter);
      }
      if (searchAdmin) {
        params.append('admin_email', searchAdmin);
      }
      if (searchTarget) {
        params.append('target_email', searchTarget);
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/ultra-admin/impersonation-logs?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Fetch logs error:', error);
      toast.error('Loglar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [actionFilter, pagination.skip]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Handle search
  const handleSearch = () => {
    setPagination({ ...pagination, skip: 0 });
    fetchLogs();
  };

  // Handle reset filters
  const handleReset = () => {
    setSearchAdmin('');
    setSearchTarget('');
    setActionFilter('all');
    setPagination({ ...pagination, skip: 0 });
    setTimeout(fetchLogs, 100);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
            <ShieldAlertIcon className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Impersonation Logları</h1>
            <p className="text-gray-600 mt-1">Kullanıcı olarak giriş yapma geçmişi</p>
          </div>
        </div>
        
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all disabled:opacity-50"
        >
          <RefreshCwIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Yenile</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Toplam İşlem</p>
              <p className="text-2xl font-bold text-blue-900">{pagination.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <ShieldAlertIcon className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Giriş İşlemleri</p>
              <p className="text-2xl font-bold text-green-900">
                {logs.filter(log => log.action === 'login').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <LogInIcon className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">Çıkış İşlemleri</p>
              <p className="text-2xl font-bold text-orange-900">
                {logs.filter(log => log.action === 'logout').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
              <LogOutIcon className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Admin Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Email
            </label>
            <Input
              placeholder="Admin ara..."
              value={searchAdmin}
              onChange={(e) => setSearchAdmin(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          {/* Target User Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hedef Kullanıcı
            </label>
            <Input
              placeholder="Kullanıcı ara..."
              value={searchTarget}
              onChange={(e) => setSearchTarget(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          {/* Action Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              İşlem Türü
            </label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">Tümü</option>
              <option value="login">Giriş</option>
              <option value="logout">Çıkış</option>
            </select>
          </div>

          {/* Buttons */}
          <div className="flex items-end space-x-2">
            <button
              onClick={handleSearch}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
            >
              <SearchIcon className="w-4 h-4" />
              <span>Ara</span>
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Sıfırla
            </button>
          </div>
        </div>
      </Card>

      {/* Logs Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCwIcon className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loglar yükleniyor...</p>
          </div>
        ) : logs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarih & Saat
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlem
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ultra Admin
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hedef Kullanıcı
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Şirket
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Adresi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 text-sm text-gray-900">
                        <CalendarIcon className="w-4 h-4 text-gray-400" />
                        <span>{formatDate(log.timestamp)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {log.action === 'login' ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          <LogInIcon className="w-3 h-3 mr-1" />
                          Giriş
                        </Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                          <LogOutIcon className="w-3 h-3 mr-1" />
                          Çıkış
                        </Badge>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-semibold">
                            {log.admin_email?.charAt(0).toUpperCase() || 'A'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{log.admin_email}</p>
                          <p className="text-xs text-gray-500">Ultra Admin</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <UserIcon className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{log.target_user_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <BuildingIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{log.company_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <GlobeIcon className="w-4 h-4 text-gray-400" />
                        <span className="font-mono">{log.ip_address || 'N/A'}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <ShieldAlertIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Log Bulunamadı</h3>
            <p className="text-gray-500">Henüz hiç impersonation işlemi yapılmamış.</p>
          </div>
        )}
      </Card>

      {/* Pagination Info */}
      {logs.length > 0 && (
        <div className="text-center text-sm text-gray-600">
          Toplam {pagination.total} kayıttan {logs.length} tanesi gösteriliyor
        </div>
      )}
    </div>
  );
};

export default ImpersonationLogs;
